import CreateAxiosInstance from './axiosConfig';
import { getStreamAuthHeaders, parseSSEStream, getAPIBaseURL } from '../utils/sseStream';

const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

/**
 * Get AI-generated autocomplete suggestions based on partial input
 * @param {Object} data - The request data
 * @param {string} data.query - The partial text the user has typed
 * @param {Object} data.context - Additional context (selected standards, file info, etc.)
 * @returns {Promise<Object>} The autocomplete response with suggestions array
 */
export async function getAutocompleteSuggestions({ query, context = {}, source }) {
  const response = await APIClient.post(
    '/unlimited/autocomplete',
    {
      query,
      context,
      num_suggestions: 4,
      ...(source && { source }),
    },
    { customErrorHandler: () => {} }
  );

  return response.data; // { suggestions: [...], book_suggestions?: [...] }
}

/**
 * Get the next follow-up question to gather additional context before creation.
 * Questions are generated one at a time, with each informed by previous Q&A context.
 *
 * @param {Object} data - The request data
 * @param {string} data.request - The user's initial request
 * @param {Array} data.previous_qa - Array of previous Q&A: [{question, answers}, ...]
 * @param {string} data.uploaded_pdf_url - Optional URL of an uploaded PDF (only used on first call)
 * @param {string[]} data.uploaded_image_urls - Optional array of uploaded image URLs
 * @param {string[]} data.urls - Optional array of URLs for text extraction (only used on first call)
 * @param {Object} data.source_content - Pass back extracted content on subsequent calls
 * @param {Object} data.detected_book_match - Auto-detected book match from user query
 * @param {Object} data.selected_book - Explicitly selected book from the library
 * @param {Object} data.context - Additional context (selected standards, file info, etc.)
 * @returns {Promise<Object>} { question: {...}|null, done: boolean, source_content?: {...}, image_analyses?: [...] }
 */
export async function getFollowUpQuestions({
  request,
  previous_qa = [],
  uploaded_pdf_url,
  uploaded_image_urls,
  urls,
  source_content,
  image_analyses,
  detected_book_match,
  selected_book,
  context = {},
  generation_session_id,
  mtss_tier,
  signal,
}) {
  const response = await APIClient.post(
    '/unlimited/follow-up',
    {
      request,
      previous_qa,
      // Include extraction params whenever they're provided
      // (on first call OR when answering source_content_request)
      ...(uploaded_pdf_url && { uploaded_pdf_url }),
      ...(uploaded_image_urls && uploaded_image_urls.length > 0 && { uploaded_image_urls }),
      ...(urls && urls.length > 0 && { urls }),
      // Pass back source_content when available
      ...(source_content && { source_content }),
      // Pass back image_analyses so subsequent questions have image context
      ...(image_analyses && image_analyses.length > 0 && { image_analyses }),
      detected_book_match,
      selected_book,
      context,
      ...(generation_session_id && { generation_session_id }),
      // MTSS tier overlay; top-level field, NOT inside context.
      // Backend reads `data.get("mtss_tier")`.
      ...(mtss_tier && { mtss_tier }),
    },
    // Suppress the global error modal: every call site catches failures and
    // degrades gracefully (skips remaining questions and proceeds to create),
    // so popping a modal on top — e.g. on the anonymous 429 rate limit — would
    // block a flow that recovers on its own.
    { customErrorHandler: () => {}, ...(signal && { signal }) }
  );

  return response.data; // { question: {...}|null, done: boolean, source_content?: {...}, image_analyses?: [...] }
}

/**
 * Create new instructional materials via streaming SSE endpoint.
 * Yields events as the outline and activities are generated.
 *
 * @param {Object} params - The create request; `from_template_id` rebuilds that template for the request's topic
 * @param {Function} onEvent - Called for each SSE event: { type, ...data }
 *   Event types:
 *     stream_started  - { stream_id }
 *     outline_ready   - { packet_id, activities: [{ index, name }] }
 *     activity_ready  - { index }
 *     done            - { packet_id, redirect_url }
 *     error           - { message }
 * @returns {Promise<void>} Resolves when the stream ends
 */
export async function createResourceStream(params, onEvent, signal) {
  const headers = await getStreamAuthHeaders();
  const baseURL = await getAPIBaseURL();

  const body = {
    request: params.request,
    ...(params.reading_level_id != null && { reading_level_id: params.reading_level_id }),
    ...(params.language && { language: params.language }),
    ...(params.orientation && { orientation: params.orientation }),
    ...(params.from_template_id && { from_template_id: params.from_template_id }),
    follow_up_context: params.follow_up_context,
    source_content: params.source_content,
    book_id: params.book_id,
    start_chapter_idx: params.start_chapter_idx,
    end_chapter_idx: params.end_chapter_idx,
    language_standards_json: params.language_standards_json,
    content_standards_json: params.content_standards_json,
    // Align → Curriculum (flag-gated upstream): the lessons' identity only. Their
    // standards travel in content_standards_json — the backend takes this as
    // identity and resolves nothing.
    curriculum_json: params.curriculum_json,
    ...(params.image_analyses?.length > 0 && { image_analyses: params.image_analyses }),
    ...(params.secondary_languages?.length > 0 && {
      secondary_languages: params.secondary_languages,
    }),
    ...(params.secondary_reading_levels?.length > 0 && {
      secondary_reading_levels: params.secondary_reading_levels,
    }),
    ...(params.generation_session_id && { generation_session_id: params.generation_session_id }),
    // MTSS tier overlay; top-level field per the backend contract —
    // `_resolve_mtss_tier(data)` reads `data["mtss_tier"]`.
    ...(params.mtss_tier && { mtss_tier: params.mtss_tier }),
    // Original text handling: true = preserve the source
    // verbatim, false = never include it as written; absent = backend decides.
    // != null (not truthiness) so an explicit false survives.
    ...(params.keep_original_text != null && { keep_original_text: params.keep_original_text }),
    // Web sources mode (auto | on | off); only sent when non-default.
    ...(params.web_sources_mode && { web_sources_mode: params.web_sources_mode }),
    // Admin debug mode: run under both arms of an experiment. The backend answers
    // with JSON ({ comparison_id }) instead of an SSE stream.
    ...(params.compare && { compare: params.compare }),
  };

  const response = await fetch(`${baseURL}/unlimited/create/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    console.error('[SSE:createResourceStream] error body:', errorBody);
    let data = null;
    try {
      data = JSON.parse(errorBody);
    } catch {
      // Non-JSON body (gateway HTML, empty) — the status alone still tells callers enough.
    }
    const error = new Error(`Create stream failed: ${response.status} — ${errorBody}`);
    // Axios-shaped so one predicate covers both create transports. The anonymous
    // per-IP 429 is raised by a backend dependency before the stream opens, so it
    // lands here as a plain JSON response, never as an SSE `error` event.
    error.response = { status: response.status, data };
    throw error;
  }

  if (params.compare) {
    const data = await response.json();
    onEvent({ type: 'comparison_started', comparison_id: data.comparison_id });
    return;
  }

  for await (const event of parseSSEStream(response)) {
    if (signal?.aborted) break;
    if (event.type === 'keepalive') continue;
    onEvent(event);
    if (event.type === 'done' || event.type === 'error') break;
  }
}

/**
 * Backfill any still-pending deferred images on a packet (self-heal on load).
 *
 * Called when a packet has `image_status: 'pending'` slots but no live stream is
 * resuming — e.g. the background generator died mid-image. The backend defers to
 * an active generation and serializes concurrent calls, returning the (possibly
 * updated) blocks so the caller can patch them in.
 *
 * @param {string} packetId
 * @returns {Promise<{ status: string, blocks: Array }>}
 */
export async function resolvePendingImages(packetId) {
  const response = await APIClient.post(
    `/unlimited/packet/${packetId}/resolve-pending-images`,
    {},
    { customErrorHandler: () => {} }
  );
  return response.data; // { status, blocks }
}

/**
 * Resume a create stream on the packet page after early redirect.
 * Reads remaining activity_ready events from the Redis stream.
 *
 * @param {string} streamId - The stream_id from the create stream
 * @param {string} lastEventId - The _eventId of the last event received
 * @param {Function} onEvent - Called for each SSE event
 * @param {AbortSignal} [signal] - Optional abort signal for cleanup
 * @returns {Promise<void>} Resolves when the stream ends
 */
export async function resumeCreateStream(streamId, lastEventId, onEvent, signal) {
  const headers = await getStreamAuthHeaders();
  const baseURL = await getAPIBaseURL();

  const url = `${baseURL}/unlimited/create/stream/${streamId}/resume?last_event_id=${encodeURIComponent(lastEventId)}`;

  const response = await fetch(url, { headers, signal });

  if (!response.ok) {
    throw new Error(`Resume stream failed: ${response.status}`);
  }

  for await (const event of parseSSEStream(response)) {
    if (signal?.aborted) break;
    if (event.type === 'keepalive') continue;
    onEvent(event);
    if (event.type === 'done' || event.type === 'error') break;
  }
}
